import React, { useCallback, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { Stack, TextField, Card } from '@mui/material'
import Modal from '@/components/Modal'
import Button from '@/components/Button'
import Text from '@/components/Text'
import { LoadingButton } from '@/components/Button'
import Form, { FormItem } from '@/components/Form'
import { useMutation } from '@tanstack/react-query'
import { createOrg } from '@/api'
import { CreateOrg, Organizationinfo } from '@/types'
import ClearIcon from '@mui/icons-material/Clear'
import { FlexRow } from './index'
import { errorText } from '@/constant'
import { ThemeProvider } from '@mui/material/styles'
import { CssBaseline } from '@mui/material'
import theme from '@/theme'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

export interface CreateOrgModalProps {
  open: boolean;
  onClose: () => void;
  onOk: (newOrg: Organizationinfo) => void;
  quick?: boolean;
}

const CreateOrgModal: React.FC<CreateOrgModalProps> = (props) => {
  const { open, onClose, onOk, quick } = props
  const [formValue, setFormValue] = useState<CreateOrg>({ name: '' })
  const [hasError, setHasError] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const [quickOpen, setQuickOpen] = useState<boolean>(true)
  const createOrgMutation = useMutation(createOrg, {
    onError: (error: string) => {
      setErrorMessage(error ?? '创建空间失败')
      setLoading(false)
    },
    onSuccess: (data) => {
      onOk(data)
      handleClose()
    },
    onSettled: () => {
      setLoading(false)
    },
  })

  const handleClose = useCallback(() => {
    if (quick) {
      setQuickOpen(false)
      onClose && onClose()
    } else {
      onClose && onClose()
    }
  }, [onClose, quick])

  const handleChangeForm = useCallback((value: CreateOrg) => {
    setFormValue(value)
  }, [])

  const handleErrorChange = useCallback((validateState: boolean) => {
    setHasError(validateState)
  }, [])

  const onCreate = useCallback(() => {
    createOrgMutation.mutate(formValue)
  }, [createOrgMutation, formValue])

  return (
    <Modal
      width={'500px'}
      open={quick ? quickOpen : open}
      onClose={handleClose}
    >
      <Card sx={{ padding: '24px' }}>
        <Stack sx={{ gap: '16px' }}>
          <FlexRow sx={{ justifyContent: 'space-between' }}>
            <Text weight='bold'>创建新空间</Text>
            <ClearIcon sx={{ cursor: 'pointer' }} onClick={handleClose} />
          </FlexRow>
          <Form
            initialValue={formValue}
            // @ts-ignore
            onFormValueChange={handleChangeForm}
            // @ts-ignore
            onValidate={handleErrorChange}
            validating={false}
          >
            <FormItem
              label='name'
              text='空间名称'
              rules={{
                errorMessage: '空间名称不能为空',
                validator: (value: string) => value?.length > 0,
              }}
            >
              <TextField
                placeholder='请输入空间名称'
                fullWidth={true}
                type='text'
              />
            </FormItem>
            <Text size='xs' sx={{ color: errorText }}>
              {errorMessage}
            </Text>
          </Form>
          <FlexRow sx={{ justifyContent: 'flex-end' }}>
            <Button type='text' onClick={handleClose}>
              取消
            </Button>
            <LoadingButton
              loading={loading}
              disabled={hasError}
              buttonStyle={{
                width: '100%',
                fontSize: '16px',
              }}
              onClick={onCreate}
            >
              创建空间
            </LoadingButton>
          </FlexRow>
        </Stack>
      </Card>
    </Modal>
  )
}

export default CreateOrgModal

export const quickCreateOrg = (
  config: Omit<CreateOrgModalProps, 'open' | 'onClose'>
) => {
  const modalDom = document.createElement('div')
  const container = document.getElementById('root')
  // @ts-ignore
  container.appendChild(modalDom)
  modalDom.id = 'org-modal-window'
  modalDom.style.zIndex = '-2'
  const modalRoot = createRoot(modalDom)
  const open = true

  const queryClient = new QueryClient({
    defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } },
  })

  const handleCloseModal = () => {
    setTimeout(() => {
      modalDom.remove()
    }, 3000)
  }

  modalRoot.render(
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <CreateOrgModal
          {...config}
          open={open}
          quick={true}
          //eslint-disable-next-line react/jsx-no-bind
          onClose={handleCloseModal}
        />
      </QueryClientProvider>
    </ThemeProvider>
  )
}
